import { call, put, takeEvery, select, takeLatest, take, debounce, fork } from 'redux-saga/effects';

import { restClient } from 'config/store/configureStore';
import { USER_WARNINGS } from 'constants/UserConstants';
import { putAndWait } from 'modules/app/sagas';
import { LOCALE_SWITCH } from 'modules/i18n/actionTypes';
import { createNotification } from 'modules/toast-notifications/actions';

import { computeWarnings } from 'modules/users/selectors';
import { getCurrentUserId, isImpersonatingUser } from 'modules/users/selectors/currentUser';
import { getUserById } from 'selectors/userSelectors';
import { downloadFile } from 'services/Utils';

import * as actions from './actions';
import * as types from './actionTypes';
import impersonateUserSagas from './impersonate.sagas';
import dashboardCustomizationSaga from './sagas/dashboardCustomization';
import { updateUser } from '../../actions/userActions';

function* fetchUsers({ payload, meta }) {
  const { request } = payload;
  try {
    const response = yield call(restClient.request, request);
    const page = request.params ? { number: request.params.page, size: request.params.size } : {};
    yield put(actions.getUsersSuccess({ page, ...response.data }, meta));
  } catch (e) {
    yield put(actions.getUsersFailure(e, meta));
  }
}

function* patchUser({ payload, meta }) {
  const { request } = payload;
  try {
    const response = yield call(restClient.request, request);
    yield put(actions.patchUserSuccess(response.data, meta));
  } catch (e) {
    yield put(actions.patchUserFailure(e));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'http.error.message',
          defaultMessage:
            'Oops! Something went wrong. Please try again or contact the administrator if the problem persists.',
        },
      })
    );
  }
}

function* afterChangeUXLanguage({ payload: locale }) {
  const userId = yield select(getCurrentUserId);
  const impersonate = yield select(isImpersonatingUser);

  if (userId && !impersonate) {
    yield put(actions.patchUser(userId, 'language', locale));
  }
}

/**
 * @param {import('modules/users/types/actions').DownloadUsersSheetRequestAction} action
 */
function* downloadUsersSheet({ payload }) {
  const { request } = payload;

  try {
    const { data } = yield call(restClient.request, request);
    yield call(downloadFile, data, {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      fileName: 'users-bulk-insert-sheet.xlsx',
    });
  } catch (e) {
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.fiche.medias.error.download',
          defaultMessage: 'An error occurred during the download.',
        },
      })
    );
  }
}

function* checkUser({ payload, meta }) {
  try {
    const { data } = yield call(restClient.request, payload.request);
    yield put(
      actions.checkUserSuccess(
        {
          ...data,
          id: payload.id,
        },
        meta
      )
    );
  } catch (error) {
    yield put(actions.checkUserFailure(error, { userId: payload.id }));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'http.error.message',
          defaultMessage:
            'Oops! Something went wrong. Please try again or contact the administrator if the problem persists.',
        },
      })
    );
  }
}

const WARNING_VALIDATIONS = [types.CHECKS_MODAL_APPROVE, types.CHECKS_MODAL_REJECT];

/** @param {import('modules/users/types/actions').CheckAndUpdateAction} action */
function* checkAndUpdateUser({ payload }) {
  const { user } = payload;
  const { status } = yield select(state => getUserById(state, user.id));
  const { error, payload: response } = yield putAndWait(actions.checkUser(user));

  if (!error) {
    // Check devices and roles
    const warnings = computeWarnings(response);
    if (Object.keys(warnings).length > 0) {
      yield put(actions.addUserWarning(warnings));
      const { type } = yield take(WARNING_VALIDATIONS);
      if (type === types.CHECKS_MODAL_REJECT) {
        return;
      }
    }

    // Check user status
    if (status !== user.status) {
      const warning = { inactive: { dealerIds: user.dealerIds, roles: user.roles, status: user.status } };
      yield put(actions.addUserWarning(warning));
      const { type } = yield take(WARNING_VALIDATIONS);
      if (type === types.CHECKS_MODAL_REJECT) {
        return;
      }
    }

    // Update user with new roles
    const { [USER_WARNINGS.DELETED_ROLES]: deletedRoles = [] } = response;
    const updatedUser = { ...user, roles: user.roles.filter(role => !deletedRoles.includes(role)) };
    // eslint-disable-next-line consistent-return
    return yield put(updateUser({ user: updatedUser }));
  }
}

const initUserSelectorMatcher = ({ type, meta }) => type === types.FETCH_USERS_REQUEST && meta?.init && meta.selectId;

const fetchUserRequestMatcher = ({ type, meta }) => type === types.FETCH_USERS_REQUEST && !meta?.init;

export default function* usersSagas() {
  yield takeEvery(types.PATCH_USER, patchUser);
  yield takeEvery(LOCALE_SWITCH, afterChangeUXLanguage);
  yield takeLatest(types.DOWNLOAD_USER_SHEET_REQUEST, downloadUsersSheet);
  yield takeLatest(types.CHECK_USER_REQUEST, checkUser);
  yield takeLatest(types.CHECK_AND_UPDATE, checkAndUpdateUser);

  yield takeEvery(initUserSelectorMatcher, fetchUsers);
  yield debounce(1000, fetchUserRequestMatcher, fetchUsers);

  yield fork(impersonateUserSagas);
  yield fork(dashboardCustomizationSaga);
}
